import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { ENQUIRY_METHOD_LIST } from '@/common/constants/common';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditPreAdmissionDetailsForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {};

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			enquiryDate: Yup.string()
				.required(t('errorMessage.pleaseSelect'))
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			enquiryMethod: Yup.string().required(t('errorMessage.pleaseSelect')),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='enquiryDate'
									label={t('preAdmission.enquiryDate') + '*'}
								/>
							</Grid>

							<Grid size={6}>
								<AppDropDownMenu
									control={methods.control}
									name='enquiryMethod'
									label={t('preAdmission.enquiryMethod') + '*'}
									options={ENQUIRY_METHOD_LIST}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditPreAdmissionDetailsForm.displayName = 'EditPreAdmissionDetailsForm';

export default EditPreAdmissionDetailsForm;
